import React, { useMemo, useEffect, useState, useContext } from 'react';
import { useNavigateParams } from '../../../hooks/useNavigateParams';

import { Context } from '../../../context/UserContext';
import Form from './Form';
import CaixaSisPanel from './CaixaSisPanel';
import { useCaixaSisConsulta } from './modules/useCaixaSisConsulta';
import { Alert, Button } from '@mui/material';
import styles from '../../form/Form.module.css';

import useFlashMessage from '../../../hooks/useFlashMessage';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';

import { useCaixaModule } from './modules/useCaixaModule';

import CrudsAPI from '../../../api/CRUDs';
import * as DataListAPI from '../../../api/dataList';
import * as EmpresaAPI from '../../../api/empresa';
import * as FinanceiroAPI from '../../../api/financeiro';
import * as PessoaAPI from '../../../api/pessoa';
import * as SituacaoAPI from '../../../api/situacao';
import * as TabelaAPI from '../../../api/tabela';

import { resolveEmpresaTabela } from '../../../utils/funcoes';
import { resolveCaixaProfile } from './profiles';

/* boot */
import { applyCaixaBoot } from './boot/applyCaixaBoot';

const CreateOrEdit = () => {
    const [dados, setDados] = useState({});

    const token = localStorage.getItem('token');
    const { parametro } = useContext(Context);
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const urlParams = new URLSearchParams(window.location.search);

    const profile = useMemo(() => resolveCaixaProfile(), []);

    // escopo empresa
    const { pessoaId, empresaId, empresaIds } = getEmpresaScope();
    const empresaTabelaId = resolveEmpresaTabela(parametro, empresaId);

    const pathname = '/Caixa';
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnText = urlParams.get('btnText');
    const sisConsulta = useCaixaSisConsulta(uuid);

    const params = new URLSearchParams({
        ul,
        li,
        tipo,
        operacao: operacaoUUID,
        uuid,
        btnText,
    });

    // -------------------------
    // options "globais" da tela
    // -------------------------
    const [optionsColaborador, setOptionsColaborador] = useState([]);
    const [Colaborador, setColaborador] = useState({});

    const [optionsTipo, setEnumTipo] = useState([]);
    const [optionsEmpresa, setEmpresa] = useState([]);
    const [optionsContaCaixa, setContasCaixa] = useState([]);
    const [optionsSituacao, setOptionsSituacao] = useState([]);
    const [optionsConta, setContas] = useState([]);
    const [optionsForma, setOptionsForma] = useState([]);

    // -------------------------
    // consultas (mantidas)
    // -------------------------
    const consultaEnum = async (table, field, filter) => {
        const result = await TabelaAPI.enums(table, field, filter);
        if (table === 'caixaLancamento') {
            if (field === 'tipo') setEnumTipo(result ? result : []);
        }
    };

    const consultaEmpresa = async () => {
        const result = await EmpresaAPI.empresa(empresaId);
        setEmpresa(result);
    };

    const consultaConta = async (empresaIdsParam, caixa) => {
        const result = await FinanceiroAPI.buscaContaByEmpresaIds({ empresaIds: empresaIdsParam, caixa });
        caixa === true ? setContasCaixa(result) : setContas(result);
        return result;
    };
    const consultaForma = async (empresaIds) => {
        if (!Array.isArray(empresaIds) || empresaIds.length === 0) {
            console.warn('empresaIds deve ser um array não vazio.');
            return;
        }

        try {
            const result = await FinanceiroAPI.buscaFormaByEmpresaIds(empresaIds);
            setOptionsForma(result);
        } catch (error) {
            console.error('Erro ao consultar empresaIds:', error);
            throw error; // Lança o erro para tratamento externo
        }
    };
    const consultaSituacao = async ({ empresaId: empId, campo, campos, operacaoSigla }) => {
        const { data } = await SituacaoAPI.situacao({ empresaId: empId, campo, campos, operacaoSigla });
        setOptionsSituacao(data);
    };

    const consultaDatalist = async ({
        separador,
        empresaId: empId,
        tabela,
        id,
        descricao,
        condicao,
        filtro,
        ativo,
        tipo: tipoDL,
    }) => {
        if (separador === 'colaborador') {
            const data = await DataListAPI.dataListColaborador({ empresaId: empId, filtro, ativo, tipo: tipoDL });
            setOptionsColaborador(data);
            return data;
        }
        return await DataListAPI.dataList({ empresaId: empId, tabela, id, descricao, condicao, filtro });
    };

    async function retornaParametroPessoa() {
        try {
            const resultado = await PessoaAPI.parametroPessoa({ id: pessoaId });

            if (!resultado) return;

            setDados((prev) => ({
                ...prev,
                colaboradorId: resultado.id,
            }));

            setColaborador((prev) => ({
                ...prev,
                descricao: resultado.descricao,
            }));

            return resultado;
        } catch (erro) {
            console.error(erro);
        }
    }

    // -------------------------
    // auth guard
    // -------------------------
    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
        }
    }, [token, navigate]);

    // Atualiza a conta quando mudar a empresa
    useEffect(() => {
        consultaConta([dados.empresaId], false);
        consultaConta([dados.empresaId], true);
        consultaForma(empresaIds);
    }, [dados.empresaId]);

    // -------------------------------
    // load inicial (mantém seu fluxo)
    // -------------------------------
    useEffect(() => {
        consultaEmpresa();
        consultaConta([empresaId], false);
        consultaConta([empresaId], true);
        consultaSituacao({ empresaId: empresaTabelaId, campo: 'caixa' });
        consultaEnum('caixaLancamento', 'tipo', '');

        if (uuid && uuid !== 'null') {
            CrudsAPI.caixa
                .consulta(uuid)
                .then((response) => {
                    const caixa = response.data;
                    setDados((prev) => ({
                        ...prev, // preserva totais já carregados
                        ...caixa, // atualiza cabeçalho real
                        // se o back vier sem totais, não apaga
                        totalRecebido: caixa?.totalRecebido ?? prev.totalRecebido,
                        totalPago: caixa?.totalPago ?? prev.totalPago,
                        totaisPorForma: caixa?.totaisPorForma ?? prev.totaisPorForma,
                    }));
                    setColaborador(caixa?.Colaborador ?? {});
                })
                .catch((error) => {
                    console.error(error);
                    setFlashMessage(error.response.data.message, error.response.data.type);
                });
            return;
        }

        // novo
        retornaParametroPessoa();

        // ✅ boot SOMENTE do caixa
        const base = applyCaixaBoot({ prev: {}, parametro, empresaId, pessoaId, now: new Date() });
        setDados((prev) => ({ ...prev, ...base }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    // ---------------------------
    // module do cabeçalho (caixa)
    // ---------------------------
    const caixaModule = useCaixaModule({
        dados,
        setDados,
        btnText,
        consultaDatalist,
        setColaborador,
        setOptionsColaborador,
        optionsColaborador,
        Colaborador,
        optionsContaCaixa,
        optionsEmpresa,
        optionsSituacao,
        setFlashMessage,
    });

    async function managerDados(obj) {
        if (uuid && uuid !== 'null' && (sisConsulta.carregando || sisConsulta.erro || sisConsulta.data?.sis !== false)) {
            setFlashMessage('Este caixa deve ser consultado e processado pelo painel SIS.', 'warning');
            return;
        }
        let urlNavigate = pathname;

        try {
            let response;

            if (uuid && uuid !== 'null') {
                response = await CrudsAPI.caixa.editar(obj);
            } else {
                response = await CrudsAPI.caixa.cadastrar(obj);

                if (response.status === 202) {
                    setDados(response.data);
                    response.data.message = 'Registro em duplicidade!';
                    response.data.type = 'warning';
                    urlNavigate = pathname + '/CreateOrEdit';
                    params.set('uuid', response.data.uuid);
                    params.set('btnText', 'Atualizar');
                }
            }

            setFlashMessage(response.data.message, response.data.type);

            setTimeout(() => {
                navigate(urlNavigate, params);
            }, parametro.msgTempo * 1000);
        } catch (error) {
            console.error(error);
            setFlashMessage(error.response?.data?.message || error.message, error.response?.data?.type || 'error');
        }
    }

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            {sisConsulta.data?.sis === true ? (
                <CaixaSisPanel
                    data={sisConsulta.data}
                    erroConsulta={sisConsulta.erro}
                    carregando={sisConsulta.carregando}
                    atualizar={sisConsulta.atualizar}
                    voltar={() => navigate(pathname, params)}
                />
            ) : sisConsulta.carregando ? (
                <Alert severity="info">Identificando o caixa…</Alert>
            ) : sisConsulta.erro ? (
                <Alert
                    severity="error"
                    action={<Button onClick={() => sisConsulta.atualizar().catch(() => {})}>Tentar novamente</Button>}
                >
                    {sisConsulta.erro}
                </Alert>
            ) : (
                <Form
                    dados={dados}
                    setDados={setDados}
                    caixaModule={caixaModule}
                    handleSubmit={managerDados}
                    profile={profile}
                    btnText={btnText}
                    caminhoVoltar={pathname}
                    parametro={parametro}
                    // globais necessários pro lançamento (module será criado no Form)
                    empresaId={empresaId}
                    empresaIds={empresaIds}
                    empresaTabelaId={empresaTabelaId}
                    pessoaId={pessoaId}
                    optionsConta={optionsConta}
                    optionsForma={optionsForma}
                    optionsTipo={optionsTipo}
                    navigate={navigate}
                    setFlashMessage={setFlashMessage}
                />
            )}
        </section>
    );
};

export default CreateOrEdit;
